import { useCallback, useEffect, useState } from "react";
import { apiService } from "../services/api.service";
import type { FilterOptions } from "../types/admin.types";

/**
 * フィルタ用参照データの状態
 */
type FilterOptionsState = {
  /** フィルタオプション（初回ロード前はnull） */
  data: FilterOptions | null;
  /** ロード中フラグ */
  isLoading: boolean;
  /** エラーメッセージ */
  error: string | null;
};

/**
 * useFilterOptions フックの戻り値
 */
interface UseFilterOptionsReturn extends FilterOptionsState {
  /** データを再取得する */
  refetch: () => Promise<void>;
}

// キャッシュ用のシングルトン
let cachedData: FilterOptions | null = null;
let cacheTimestamp = 0;
// キャッシュ有効期限（5分）
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * フィルタ用参照データを取得・キャッシュするカスタムフック
 *
 * - 初回ロード時にAPIからデータを取得
 * - 5分間キャッシュ（モジュールスコープのシングルトン）
 * - 複数コンポーネントで使用しても1回のAPIコールで済む
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useFilterOptions();
 *
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error} />;
 * if (!data) return null;
 *
 * return (
 *   <Select options={data.teams.map(t => ({ value: t.id, label: t.name }))} />
 * );
 * ```
 */
export function useFilterOptions(): UseFilterOptionsReturn {
  const [state, setState] = useState<FilterOptionsState>({
    data: cachedData,
    isLoading: cachedData === null,
    error: null,
  });

  const fetchData = useCallback(async () => {
    // キャッシュが有効な場合は使用
    const now = Date.now();
    if (cachedData && now - cacheTimestamp < CACHE_TTL_MS) {
      setState({
        data: cachedData,
        isLoading: false,
        error: null,
      });
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await apiService.getFilterOptions();
      cachedData = data;
      cacheTimestamp = Date.now();
      setState({
        data,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "フィルタオプションの取得に失敗しました";
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // 初回マウント時にデータを取得
  useEffect(() => {
    // キャッシュが有効な場合はスキップ
    const now = Date.now();
    if (cachedData && now - cacheTimestamp < CACHE_TTL_MS) {
      if (state.data !== cachedData) {
        setState({
          data: cachedData,
          isLoading: false,
          error: null,
        });
      }
      return;
    }

    fetchData();
  }, [fetchData, state.data]);

  const refetch = useCallback(async () => {
    // キャッシュを無効化して再取得
    cachedData = null;
    cacheTimestamp = 0;
    await fetchData();
  }, [fetchData]);

  return {
    ...state,
    refetch,
  };
}

/**
 * キャッシュをクリアする（テスト用）
 */
export function clearFilterOptionsCache(): void {
  cachedData = null;
  cacheTimestamp = 0;
}
